import { toLevelFootprintPolygons } from '@alpinehomeair/common';
import {
  FootprintPolygonsType,
  LevelType,
  PositionAndDimensionsType,
  PositionType
} from 'data/types';
import {
  clampRectToFootprint,
  constrainResizeToFootprint,
  isPointInPolygon,
  isRectInPolygon,
  polygonBoundingBox
} from 'utils/footprint';
import {
  FootprintBounds,
  FootprintRectLike
} from 'utils/footprint-polygons-types';
import { calculateFootprintArea } from 'utils/footprint-scaling';
import { isValidFootprintShape } from 'utils/footprint-union';
import type { ResizeDirection } from 'utils/grid';

export const EMPTY_FOOTPRINT_POLYGONS: FootprintPolygonsType = [];
const EMPTY_POLYGONS = EMPTY_FOOTPRINT_POLYGONS;
const polygonsByShapes = new WeakMap<object, FootprintPolygonsType>();

function memoKey(level: LevelType): object | null {
  if (level.footprintShapes && level.footprintShapes.length > 0) {
    return level.footprintShapes;
  }
  return level.footprint ?? null;
}

export function getEffectiveFootprintPolygons(
  level: LevelType | null | undefined
): FootprintPolygonsType {
  if (!level) return EMPTY_POLYGONS;
  const key = memoKey(level);
  if (!key) return EMPTY_POLYGONS;
  const cached = polygonsByShapes.get(key);
  if (cached) return cached;
  const polygons = toLevelFootprintPolygons(level);
  polygonsByShapes.set(key, polygons);
  return polygons;
}

export function hasFootprintPolygons(
  level: LevelType | null | undefined
): boolean {
  return getEffectiveFootprintPolygons(level).length > 0;
}

export function hasAnyFootprintPolygon(
  polygons: FootprintPolygonsType | null | undefined
): boolean {
  return !!polygons && polygons.some(isValidFootprintShape);
}

export function getFootprintPolygonsArea(
  polygons: FootprintPolygonsType
): number {
  return polygons.reduce(
    (sum, polygon) => sum + calculateFootprintArea(polygon),
    0
  );
}

export function getLevelFootprintArea(
  level: LevelType | null | undefined
): number {
  return getFootprintPolygonsArea(getEffectiveFootprintPolygons(level));
}

export function footprintBoundingBox(
  polygons: FootprintPolygonsType
): FootprintBounds | null {
  const points = polygons.flat();
  if (points.length === 0) return null;
  return polygonBoundingBox(points);
}

export function footprintBoundsCenter(bounds: FootprintBounds): PositionType {
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2
  };
}

export function isPointInAnyPolygon(
  point: PositionType,
  polygons: FootprintPolygonsType
): boolean {
  return polygons.some((polygon) => isPointInPolygon(point, polygon));
}

export function isRectInAnyPolygon(
  rect: PositionAndDimensionsType,
  polygons: FootprintPolygonsType
): boolean {
  if (polygons.length === 0) return true;
  return polygons.some((polygon) => isRectInPolygon(rect, polygon));
}

function rectBounds(rect: FootprintRectLike): FootprintBounds {
  return {
    minX: rect.x,
    minY: rect.y,
    maxX: rect.x + rect.width,
    maxY: rect.y + rect.height
  };
}

function boundsOverlapArea(a: FootprintBounds, b: FootprintBounds): number {
  const width = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const height = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  if (width <= 0 || height <= 0) return 0;
  return width * height;
}

function largestBoundsOverlap(
  bounds: FootprintBounds,
  polygons: FootprintPolygonsType
): PositionType[] | null {
  let best: PositionType[] | null = null;
  let bestArea = 0;
  polygons.forEach((polygon) => {
    const area = boundsOverlapArea(bounds, polygonBoundingBox(polygon));
    if (area > bestArea) {
      best = polygon;
      bestArea = area;
    }
  });
  return best;
}

function nearestCenter(
  center: PositionType,
  polygons: FootprintPolygonsType
): PositionType[] | null {
  let best: PositionType[] | null = null;
  let bestDistance = Infinity;
  polygons.forEach((polygon) => {
    const polygonCenter = footprintBoundsCenter(polygonBoundingBox(polygon));
    const distance = Math.hypot(
      polygonCenter.x - center.x,
      polygonCenter.y - center.y
    );
    if (distance < bestDistance) {
      best = polygon;
      bestDistance = distance;
    }
  });
  return best;
}

export function findContainingPolygon(
  rect: FootprintRectLike,
  polygons: FootprintPolygonsType
): PositionType[] | null {
  if (polygons.length === 0) return null;
  if (polygons.length === 1) return polygons[0];

  const containing = polygons.find((polygon) =>
    isRectInPolygon(
      { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
      polygon
    )
  );
  if (containing) return containing;

  const bounds = rectBounds(rect);
  return (
    largestBoundsOverlap(bounds, polygons) ??
    nearestCenter(footprintBoundsCenter(bounds), polygons)
  );
}

export function findPolygonForPoints(
  points: PositionType[],
  polygons: FootprintPolygonsType
): PositionType[] | null {
  if (polygons.length === 0 || points.length === 0) return null;
  if (polygons.length === 1) return polygons[0];
  const bounds = polygonBoundingBox(points);
  return findContainingPolygon(
    {
      x: bounds.minX,
      y: bounds.minY,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY
    },
    polygons
  );
}

export function clampRectToFootprints(
  rect: PositionAndDimensionsType,
  polygons: FootprintPolygonsType
): PositionAndDimensionsType {
  const polygon = findContainingPolygon(rect, polygons);
  if (!polygon) return rect;
  return clampRectToFootprint(rect, polygon);
}

export function constrainResizeToFootprints(
  position: PositionAndDimensionsType,
  polygons: FootprintPolygonsType,
  direction: ResizeDirection
): PositionAndDimensionsType {
  const polygon = findContainingPolygon(position, polygons);
  if (!polygon) return position;
  return constrainResizeToFootprint(position, polygon, direction);
}

export function legacyFootprintToPolygons(
  footprint: PositionType[] | null | undefined
): FootprintPolygonsType {
  return footprint && isValidFootprintShape(footprint)
    ? [footprint]
    : EMPTY_POLYGONS;
}
